import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { collection, query, where, getDocs, doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';

/**
 * ADMIN Registrations list — per-registrant detail for one sale. AdminRoute-gated (NO
 * consignor): this page renders BIDDER PII (name, email, bidder number).
 *
 * 3-way join: saleRegistrations (uid/email/name/registeredAt/cardAuthRan)
 *           + users/{uid}      (bidderNumber, displayName)
 *           + bids             (per-bidder bid count, voided excluded)
 */

interface Row {
  uid: string;
  bidderNumber: string;
  name: string;
  email: string;
  registeredAt: Date | null;
  bidCount: number;
  authStatus: string;
  paymentType: 'card' | 'cash'; // absent on the user doc => 'card' (default)
}

const SaleRegistrations: React.FC = () => {
  const { auctionId } = useParams<{ auctionId: string }>();
  const { currentUser } = useAuth();
  const [rows, setRows] = useState<Row[]>([]);
  const [title, setTitle] = useState('');
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [savingUid, setSavingUid] = useState<string | null>(null);

  // Admin-only: set a bidder's payment type. Server-enforced by firestore.rules
  // (isPaymentStaff = admin/auctioneer); a non-admin viewer gets a permission error,
  // which we surface as a toast rather than a silent failure.
  const setPaymentType = async (uid: string, next: 'card' | 'cash') => {
    setSavingUid(uid);
    try {
      await updateDoc(doc(db, 'users', uid), {
        paymentType: next,
        paymentTypeSetAt: serverTimestamp(),
        paymentTypeSetBy: currentUser?.uid ?? null,
      });
      setRows((prev) => prev.map((r) => (r.uid === uid ? { ...r, paymentType: next } : r)));
      toast.success(`Payment type set to ${next.toUpperCase()}`);
    } catch (e) {
      console.error('setPaymentType failed', e);
      toast.error('Could not update payment type — admin/auctioneer only.');
    } finally {
      setSavingUid(null);
    }
  };

  useEffect(() => {
    let active = true;
    (async () => {
      if (!auctionId) return;
      try {
        const [aSnap, regSnap, bidSnap] = await Promise.all([
          getDoc(doc(db, 'auctions', auctionId)),
          getDocs(query(collection(db, 'saleRegistrations'), where('auctionId', '==', auctionId))),
          getDocs(query(collection(db, 'bids'), where('auctionId', '==', auctionId))),
        ]);
        if (!active) return;
        setTitle((aSnap.exists() && (aSnap.data() as any).title) || auctionId);

        // Bid counts per bidder (voided excluded).
        const bidCounts = new Map<string, number>();
        bidSnap.forEach((d) => {
          const b: any = d.data();
          if (b.status === 'rejected' || b.status === 'pending' || b.voidReason) return;
          if (!b.bidderId) return;
          bidCounts.set(b.bidderId, (bidCounts.get(b.bidderId) ?? 0) + 1);
        });

        const regs = regSnap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
        const users = await Promise.all(
          regs.map(async (r) => {
            try {
              const u = await getDoc(doc(db, 'users', r.uid));
              return u.exists() ? (u.data() as any) : null;
            } catch { return null; }
          })
        );
        if (!active) return;

        const out: Row[] = regs.map((r, i) => {
          const u = users[i] || {};
          const authStatus = r.cardAuthRan === true
            ? '$0 auth (this sale)'
            : u?.authnet?.cardVerifiedAt
              ? 'Card on file'
              : '—';
          return {
            uid: r.uid,
            bidderNumber: u.bidderNumber || '—',
            name: r.name || u.displayName || '—',
            email: r.email || u.email || '—',
            registeredAt: r.registeredAt?.toDate?.() ?? null,
            bidCount: bidCounts.get(r.uid) ?? 0,
            authStatus,
            // ABSENT => 'card' (default). Cash is only ever an explicit admin write.
            paymentType: (u.paymentType === 'cash' ? 'cash' : 'card') as 'card' | 'cash',
          };
        });
        out.sort((a, b) => (b.registeredAt?.getTime() ?? 0) - (a.registeredAt?.getTime() ?? 0));
        setRows(out);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [auctionId]);

  const visible = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      r.name.toLowerCase().includes(q) ||
      r.email.toLowerCase().includes(q) ||
      String(r.bidderNumber).toLowerCase().includes(q)
    );
  }, [rows, filter]);

  if (loading) return <div className="text-center py-12 text-gray-600">Loading registrations…</div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold">Registrations ({rows.length})</h1>
          <p className="text-gray-600">{title}</p>
        </div>
        <div className="flex gap-2">
          <input
            className="input py-2"
            placeholder="Filter name / email / bidder #"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          <Link to={`/admin/sale/${auctionId}/dashboard`} className="btn-secondary">Dashboard</Link>
          <Link to={`/admin/sale/${auctionId}/lot-stats`} className="btn-secondary">Lot Stats</Link>
        </div>
      </div>

      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b bg-gray-50">
              <th className="py-2 px-2">Bidder #</th>
              <th className="py-2 px-2">Name</th>
              <th className="py-2 px-2">Email</th>
              <th className="py-2 px-2">Registered</th>
              <th className="py-2 px-2 text-right">Bids</th>
              <th className="py-2 px-2">Card</th>
              <th className="py-2 px-2">Payment</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((r) => (
              <tr key={r.uid} className="border-b last:border-0 hover:bg-gray-50">
                <td className="py-2 px-2 font-mono">#{r.bidderNumber}</td>
                <td className="py-2 px-2 font-semibold">{r.name}</td>
                <td className="py-2 px-2 text-gray-700">{r.email}</td>
                <td className="py-2 px-2 text-gray-600">{r.registeredAt ? r.registeredAt.toLocaleString() : '—'}</td>
                <td className="py-2 px-2 text-right font-semibold">{r.bidCount}</td>
                <td className="py-2 px-2">
                  <span className={`text-xs px-2 py-0.5 rounded ${
                    r.authStatus === '—' ? 'bg-gray-100 text-gray-600' : 'bg-green-100 text-green-800'
                  }`}>
                    {r.authStatus}
                  </span>
                </td>
                <td className="py-2 px-2">
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-bold px-2 py-0.5 rounded ${
                      r.paymentType === 'cash' ? 'bg-amber-100 text-amber-800' : 'bg-blue-100 text-blue-800'
                    }`}>
                      {r.paymentType === 'cash' ? 'CASH' : 'CARD'}
                    </span>
                    <button
                      type="button"
                      disabled={savingUid === r.uid}
                      onClick={() => setPaymentType(r.uid, r.paymentType === 'cash' ? 'card' : 'cash')}
                      className="text-xs px-2 py-0.5 rounded border border-gray-300 hover:bg-gray-100 disabled:opacity-50"
                      title="Admin/auctioneer only"
                    >
                      {savingUid === r.uid ? '…' : r.paymentType === 'cash' ? 'Set Card' : 'Set Cash'}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {visible.length === 0 && (
              <tr><td colSpan={7} className="py-6 text-center text-gray-500">No registrations match.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SaleRegistrations;
